import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { CronBuilder } from './CronBuilder';
import { ManualTaskPanel } from './ManualTaskPanel';
import { WebhookTaskPanel } from './WebhookTaskPanel';
import {
  createTask,
  deleteTask,
  listTasks,
  previewSchedule,
  updateTask,
  type AgentTask,
  type AgentTaskTriggerKind,
} from './api';

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/Madrid',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Tokyo',
];

export function ScheduledTasksSection({ orgId, agentId }: { orgId: string; agentId: string }) {
  const qc = useQueryClient();
  const tasks = useQuery({
    queryKey: ['agent-tasks', orgId, agentId],
    queryFn: () => listTasks(orgId, agentId),
    enabled: !!orgId && !!agentId,
  });
  const [creating, setCreating] = useState(false);

  return (
    <Card>
      <CardHeader
        title="Triggers"
        description="Ways this agent can fire on its own. Cron triggers run on a recurring schedule (Sigil's scheduler ticks every minute); webhook triggers fire on an inbound HTTP POST from an external system (Stripe, GitHub, n8n, …). Each fire counts against the agent's daily run quota."
      />
      <CardBody className="space-y-4">
        {tasks.isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
        {tasks.data && tasks.data.length === 0 && !creating && (
          <p className="text-sm text-ink-tertiary">No triggers yet.</p>
        )}
        {tasks.data && tasks.data.length > 0 && (
          <div className="space-y-2">
            {tasks.data.map((t) => (
              <TaskRow key={t.id} orgId={orgId} agentId={agentId} task={t} onChange={() => qc.invalidateQueries({ queryKey: ['agent-tasks', orgId, agentId] })} />
            ))}
          </div>
        )}

        {creating ? (
          <TaskForm
            orgId={orgId}
            agentId={agentId}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              qc.invalidateQueries({ queryKey: ['agent-tasks', orgId, agentId] });
            }}
          />
        ) : (
          <div>
            <Button variant="secondary" onClick={() => setCreating(true)}>
              + Add a trigger
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function TaskRow({
  orgId,
  agentId,
  task,
  onChange,
}: {
  orgId: string;
  agentId: string;
  task: AgentTask;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const toggle = useMutation({
    mutationFn: () => updateTask(orgId, agentId, task.id, { enabled: !task.enabled }),
    onSuccess: onChange,
  });
  const remove = useMutation({
    mutationFn: () => deleteTask(orgId, agentId, task.id),
    onSuccess: onChange,
  });

  if (editing) {
    return (
      <TaskForm
        orgId={orgId}
        agentId={agentId}
        existing={task}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onChange();
        }}
      />
    );
  }

  const cron = (task.trigger_config?.cron_expr as string) ?? '';
  const tz = (task.trigger_config?.timezone as string) ?? 'UTC';
  const isWebhook = task.trigger_kind === 'webhook';

  return (
    <div className="rounded-lg border border-line-soft bg-surface-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-ink-primary">{task.name}</p>
            <span
              className={`rounded-full border px-1.5 py-0.5 font-mono text-[10px] uppercase ${
                task.enabled
                  ? 'border-ok/30 bg-ok/10 text-ok'
                  : 'border-line bg-surface-soft text-ink-tertiary'
              }`}
            >
              {task.enabled ? 'enabled' : 'paused'}
            </span>
            <span className="rounded border border-line-soft bg-surface-soft px-1.5 py-0.5 font-mono text-[10px] uppercase text-ink-tertiary">
              {task.trigger_kind}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-ink-secondary">{task.objective}</p>
          {!isWebhook && (
            <p className="mt-1 font-mono text-[11px] text-ink-tertiary">
              {cron ? `${cron} · ${tz}` : tz}
              {task.next_run_at && ` · next ${new Date(task.next_run_at).toLocaleString()}`}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="text-xs text-ink-secondary hover:text-ink-primary"
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
          >
            {task.enabled ? 'Pause' : 'Resume'}
          </button>
          <button
            type="button"
            className="text-xs text-ink-secondary hover:text-ink-primary"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-xs text-danger hover:underline"
            onClick={() => {
              if (window.confirm(`Delete task "${task.name}"?`)) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            Delete
          </button>
        </div>
      </div>
      {isWebhook && <WebhookTaskPanel orgId={orgId} agentId={agentId} task={task} />}
      {task.trigger_kind === 'manual' && (
        <ManualTaskPanel orgId={orgId} agentId={agentId} task={task} />
      )}
    </div>
  );
}

export function TaskForm({
  orgId,
  agentId,
  existing,
  initialTriggerKind,
  onCancel,
  onSaved,
}: {
  orgId: string;
  agentId: string;
  existing?: AgentTask;
  initialTriggerKind?: AgentTaskTriggerKind;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const initialCron = (existing?.trigger_config?.cron_expr as string) ?? '0 9 * * *';
  const initialTz =
    (existing?.trigger_config?.timezone as string) ?? guessTimezone();
  const nav = useNavigate();
  const [triggerKind, setTriggerKind] = useState<AgentTaskTriggerKind>(
    existing?.trigger_kind ?? initialTriggerKind ?? 'manual',
  );
  const [name, setName] = useState(existing?.name ?? '');
  const [objective, setObjective] = useState(existing?.objective ?? '');
  const [cronExpr, setCronExpr] = useState(initialCron);
  const [timezone, setTimezone] = useState(initialTz);

  const save = useMutation({
    mutationFn: async () => {
      const trigger_config =
        triggerKind === 'cron' ? { cron_expr: cronExpr, timezone } : {};
      if (existing) {
        return updateTask(orgId, agentId, existing.id, {
          name,
          objective,
          trigger_config,
        });
      }
      return createTask(orgId, agentId, {
        name,
        objective,
        trigger_kind: triggerKind,
        trigger_config,
        enabled: true,
      });
    },
    onSuccess: (task) => {
      // Manual tasks fire their first run inside POST /tasks — the
      // backend hands back first_run_id. Take the operator straight
      // to the run trace.
      if (triggerKind === 'manual' && task.first_run_id) {
        nav(`/agents/${agentId}/runs/${task.first_run_id}`);
        return;
      }
      onSaved();
    },
  });

  const preview = useQuery({
    queryKey: ['preview', orgId, agentId, cronExpr, timezone],
    queryFn: () => previewSchedule(orgId, agentId, cronExpr, timezone),
    enabled: triggerKind === 'cron' && !!cronExpr && !!timezone,
  });

  const cronValid = /^([^\s]+\s+){4}[^\s]+$/.test(cronExpr.trim());

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!objective.trim()) return;
    if (triggerKind !== 'manual' && !name.trim()) return;
    if (triggerKind === 'cron' && !cronExpr.trim()) return;
    save.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-line bg-surface-soft/40 p-4">
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Trigger</label>
        <div className="mt-1 inline-flex rounded-md border border-line bg-surface-white p-0.5">
          <TriggerPill
            active={triggerKind === 'manual'}
            onClick={() => setTriggerKind('manual')}
            disabled={!!existing && existing.trigger_kind !== 'manual'}
          >
            Run now
          </TriggerPill>
          <TriggerPill
            active={triggerKind === 'cron'}
            onClick={() => setTriggerKind('cron')}
            disabled={!!existing && existing.trigger_kind !== 'cron'}
          >
            Cron
          </TriggerPill>
          <TriggerPill
            active={triggerKind === 'webhook'}
            onClick={() => setTriggerKind('webhook')}
            disabled={!!existing && existing.trigger_kind !== 'webhook'}
          >
            Webhook
          </TriggerPill>
        </div>
        {triggerKind === 'webhook' && (
          <p className="mt-1 text-[11px] text-ink-tertiary">
            Fires when an external system POSTs to the task's URL. You'll get the URL + a
            bearer secret after creating it.
          </p>
        )}
        {triggerKind === 'manual' && (
          <p className="mt-1 text-[11px] text-ink-tertiary">
            One-shot run. Saving fires the agent immediately and opens its trace; the task
            stays here so you can re-run it with one click later.
          </p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Name</label>
        <input
          type="text"
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={
            triggerKind === 'cron'
              ? 'e.g. Pay invoices every morning'
              : 'e.g. Pay invoices on Stripe payment_intent.succeeded'
          }
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Objective</label>
        <textarea
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          rows={3}
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          placeholder={
            triggerKind === 'cron'
              ? 'e.g. Review the inbox for new invoices and pay any that match an approved payee.'
              : triggerKind === 'webhook'
                ? 'e.g. Process the incoming payment in the webhook body. Find the matching payee and execute the payment.'
                : 'e.g. Pay 0.05 ETH to office-rent for May 2026 invoice.'
          }
          required
        />
      </div>

      {triggerKind === 'cron' && (
        <>
          <div className="space-y-3 rounded-md border border-line-soft bg-surface-white p-3">
            <CronBuilder value={cronExpr} onChange={setCronExpr} />
            <div>
              <label className="block text-xs font-medium text-ink-secondary">Timezone</label>
              <input
                type="text"
                list="tz-options"
                className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-xs text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="UTC"
                required
              />
              <datalist id="tz-options">
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="rounded-md border border-line-soft bg-surface-white p-3">
            <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
              Next 5 occurrences
            </p>
            {preview.isLoading && <p className="mt-1 text-xs text-ink-tertiary">Calculating…</p>}
            {preview.error && (
              <p className="mt-1 text-xs text-danger">
                {(preview.error as Error).message || 'invalid schedule'}
              </p>
            )}
            {preview.data && (
              <ul className="mt-1 space-y-0.5">
                {preview.data.map((t) => (
                  <li key={t} className="font-mono text-[11px] text-ink-primary">
                    {new Date(t).toLocaleString()}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {save.error && (
        <p className="text-xs text-danger">{(save.error as Error).message || 'Could not save'}</p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={save.isPending || (triggerKind === 'cron' && !cronValid)}
        >
          {save.isPending
            ? triggerKind === 'manual'
              ? 'Running…'
              : 'Saving…'
            : existing
              ? 'Save changes'
              : triggerKind === 'manual'
                ? 'Run now'
                : triggerKind === 'webhook'
                  ? 'Create webhook'
                  : 'Schedule'}
        </Button>
      </div>
    </form>
  );
}

function TriggerPill({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-bit2me text-white'
          : 'text-ink-secondary hover:bg-surface-soft hover:text-ink-primary'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function guessTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
}
